// src/components/ProspectDiscoveryTab.jsx
// AI-powered prospect discovery tab for finding acquisition targets

import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, Loader2, Sparkles, MapPin, Phone, Globe, Star, 
  User, Building2, Flame, ChevronDown, ChevronRight, Plus,
  CheckCircle2, XCircle, Eye, Clock, DollarSign, TrendingUp,
  AlertTriangle, ExternalLink, RefreshCw, Filter, X, Info, Check
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLfgLocations } from '../hooks/useSupabase';
import { useDisplayColors } from '../lib/ThemeContext';

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

// Scoring rubric definition — single source of truth for both the prompt and the UI
const SCORING_RUBRIC = [
  { label: 'Owner appears 60+',          points: 2,   note: 'Succession opportunity' },
  { label: 'Family-owned / independent', points: 2,   note: 'Not a chain' },
  { label: 'Google rating 4.5+',         points: 1,   note: 'Strong community reputation' },
  { label: 'Established 30+ years',      points: 1,   note: 'Deep community roots' },
  { label: 'Multiple locations',         points: 1,   note: 'Built-in scale' },
  { label: 'Est. 150+ calls/year',       points: 1,   note: 'Strong call volume' },
  { label: 'Has crematory',              points: 0.5, note: 'Added service capability' },
  { label: 'Within 10 mi of LFG',        points: 0.5, note: 'Synergy / geographic fit', computed: true },
];
const MAX_SCORE = SCORING_RUBRIC.reduce((sum, r) => sum + r.points, 0); // 9.5

// Haversine distance in miles
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Consistent label+control pattern for the discovery form
function ProspectField({ label, optional, children }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-1 block" style={{ color: '#5B21B6' }}>
        {label}
        {optional && <span className="font-normal normal-case ml-1" style={{ color: '#94A3B8' }}>(optional)</span>}
      </label>
      {children}
    </div>
  );
}

// Clickable status badge with a dropdown of all four statuses. Lets the user
// move a prospect freely between New / Reviewing / Dismissed, and trigger
// the pipeline-add flow (which also creates a real deal record). Closes on
// outside click.
const PROSPECT_STATUS_META = {
  new:               { label: 'New',          dot: '#7C3AED', bg: '#F5F3FF', fg: '#6D28D9' },
  reviewing:         { label: 'Reviewing',    dot: '#3B82F6', bg: '#EFF6FF', fg: '#1D4ED8' },
  added_to_pipeline: { label: 'In Pipeline',  dot: '#10B981', bg: '#ECFDF5', fg: '#047857' },
  dismissed:         { label: 'Dismissed',    dot: '#94A3B8', bg: '#F1F5F9', fg: '#475569' },
};

function ProspectStatusMenu({ prospect, addingToPipeline, onAddToPipeline, onSetStatus }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const meta = PROSPECT_STATUS_META[prospect.status] || PROSPECT_STATUS_META.new;
  const inPipeline = prospect.status === 'added_to_pipeline';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={addingToPipeline}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all hover:shadow-sm disabled:opacity-60"
        style={{ backgroundColor: meta.bg, color: meta.fg }}
        title="Change status"
      >
        {addingToPipeline ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
        )}
        <span>{meta.label}</span>
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 rounded-lg overflow-hidden z-20 min-w-[180px] py-1"
          style={{
            backgroundColor: 'white',
            border: '1px solid #E5E9F0',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.06)',
          }}
        >
          {['new', 'reviewing', 'dismissed'].map(s => {
            const m = PROSPECT_STATUS_META[s];
            const isCurrent = prospect.status === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => { setOpen(false); onSetStatus(s); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[#FAFBFD]"
                style={{ color: '#0F172A' }}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: m.dot }} />
                <span className="flex-1">Mark as {m.label}</span>
                {isCurrent && <Check className="w-3.5 h-3.5" style={{ color: '#16A34A' }} />}
              </button>
            );
          })}
          <div style={{ borderTop: '1px solid #F0F2F6' }}>
            <button
              type="button"
              onClick={() => { setOpen(false); if (!inPipeline) onAddToPipeline(); }}
              disabled={inPipeline}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: '#047857' }}
              title={inPipeline ? 'Already in the pipeline' : 'Create a deal from this prospect'}
            >
              <Plus className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1">{inPipeline ? 'Already in pipeline' : 'Add to pipeline…'}</span>
              {inPipeline && <Check className="w-3.5 h-3.5" style={{ color: '#16A34A' }} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProspectDiscoveryTab({ 
  organizationId, 
  primaryColor, 
  secondaryColor, 
  showToast,
  onAddToPipeline
}) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(null, null);
  // Discovery form state
  const [region, setRegion] = useState('');
  const [centerCity, setCenterCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(50);
  // Two search modes: 'area' (the original — region + radius) and 'name'
  // (deep-dive on a specific funeral home by name + optional city/state).
  const [searchMode, setSearchMode] = useState('area');
  const [prospectName, setProspectName] = useState('');
  const [prospectCity, setProspectCity] = useState('');
  const [prospectStateInput, setProspectStateInput] = useState('');
  const [excludeConsolidators, setExcludeConsolidators] = useState(true);
  const [requireCrematory, setRequireCrematory] = useState(false);
  const [minCalls, setMinCalls] = useState('');
  const [maxResults, setMaxResults] = useState(15);

  // UI state
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState('');
  const [showRubric, setShowRubric] = useState(false);

  // Results state
  const [prospects, setProspects] = useState([]);
  const [pastRuns, setPastRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [expandedProspect, setExpandedProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToPipeline, setAddingToPipeline] = useState({});

  // Filters
  const [scoreFilter, setScoreFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('new');
  const [searchQuery, setSearchQuery] = useState('');

  // Dedupe modal
  const [showDedupeModal, setShowDedupeModal] = useState(false);
  const [dedupeBusy, setDedupeBusy] = useState(false);

  // LFG locations for distance calculation
  const { locations: lfgLocations } = useLfgLocations(organizationId);

  // Geocoded coordinates for prospects (keyed by prospect.id)
  const [prospectCoords, setProspectCoords] = useState({});
  const geocodingRef = useRef(false);

  // Load past discovery runs and prospects on mount
  useEffect(() => {
    loadPastRuns();
    loadProspects();
  }, [organizationId]);

  // Geocode prospects that don't yet have coordinates in our local cache
  // Throttled at 1 request/sec to respect Nominatim rate limits
  useEffect(() => {
    if (!prospects.length || geocodingRef.current) return;

    const ungeocoded = prospects.filter(p => p.city && p.state && !prospectCoords[p.id]);
    if (!ungeocoded.length) return;

    geocodingRef.current = true;

    const geocodeNext = async (index) => {
      if (index >= ungeocoded.length) {
        geocodingRef.current = false;
        return;
      }
      const p = ungeocoded[index];
      try {
        const query = encodeURIComponent(`${p.city}, ${p.state}`);
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`,
          { headers: { 'User-Agent': 'LFG-Acquisitions-Portal' } }
        );
        const data = await res.json();
        if (data?.[0]) {
          setProspectCoords(prev => ({
            ...prev,
            [p.id]: { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
          }));
        }
      } catch {
        // Silently skip — distance will just not show for this prospect
      }
      // 1-second gap between requests
      setTimeout(() => geocodeNext(index + 1), 1000);
    };

    geocodeNext(0);
  }, [prospects]);

  // Compute nearest LFG distance for a prospect (miles), or null if unknown
  const getNearestLfgDistance = (prospect) => {
    const coords = prospectCoords[prospect.id];
    if (!coords) return null;
    const lfgWithCoords = lfgLocations.filter(l => l.latitude && l.longitude);
    if (!lfgWithCoords.length) return null;
    let min = Infinity;
    lfgWithCoords.forEach(l => {
      const d = haversineDistance(coords.lat, coords.lng, l.latitude, l.longitude);
      if (d < min) min = d;
    });
    return min < Infinity ? Math.round(min) : null;
  };

  // Display score = AI score + 0.5 bonus if within 10 miles of LFG
  const getDisplayScore = (prospect) => {
    const base = prospect.acquisition_score || 0;
    const dist = getNearestLfgDistance(prospect);
    const bonus = dist !== null && dist < 10 ? 0.5 : 0;
    // Cap at MAX_SCORE
    return Math.min(base + bonus, MAX_SCORE);
  };

  const loadPastRuns = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_analyses')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('analysis_type', 'prospect_discovery')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      setPastRuns(data || []);
    } catch (err) {
      console.error('Error loading past runs:', err);
    }
  };

  const loadProspects = async (runId = null) => {
    try {
      setLoading(true);
      let query = supabase
        .from('discovered_prospects')
        .select('*')
        .eq('organization_id', organizationId)
        .order('acquisition_score', { ascending: false });
      if (runId) query = query.eq('discovery_run_id', runId);
      const { data, error } = await query;
      if (error) throw error;
      setProspects(data || []);
      // Reset geocode cache so new prospects get geocoded
      setProspectCoords({});
      geocodingRef.current = false;
    } catch (err) {
      console.error('Error loading prospects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscovery = async () => {
    let payload;
    let progressMsg;

    if (searchMode === 'name') {
      if (!prospectName.trim()) {
        showToast('Please enter a funeral home name', 'error');
        return;
      }
      payload = {
        prospect_name: prospectName.trim(),
        prospect_city: prospectCity.trim() || null,
        prospect_state: prospectStateInput || null,
        exclude_consolidators: excludeConsolidators,
      };
      progressMsg = `Researching ${prospectName.trim()}…`;
    } else {
      const regionStr = centerCity && selectedState
        ? `${centerCity}, ${selectedState}`
        : selectedState
          ? US_STATES.find(s => s.code === selectedState)?.name || selectedState
          : region;

      if (!regionStr) {
        showToast('Please enter a region or select a state', 'error');
        return;
      }

      payload = {
        region: regionStr,
        center_city: centerCity,
        radius_miles: radiusMiles,
        exclude_consolidators: excludeConsolidators,
        require_crematory: requireCrematory,
        min_calls: minCalls ? parseInt(minCalls) : null,
        max_results: maxResults,
      };
      progressMsg = `Searching for funeral homes in ${regionStr}…`;
    }

    setIsDiscovering(true);
    setDiscoveryProgress('Initializing AI discovery...');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      setDiscoveryProgress(progressMsg);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-prospect-discovery`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Discovery failed');

      setDiscoveryProgress('');
      showToast(`Found ${result.prospects_found} prospects!`);

      await loadPastRuns();
      await loadProspects();

      if (result.analysis_id) setSelectedRun(result.analysis_id);

    } catch (err) {
      console.error('Discovery error:', err);
      showToast(err.message || 'Discovery failed', 'error');
    } finally {
      setIsDiscovering(false);
      setDiscoveryProgress('');
    }
  };

  const handleAddToPipeline = async (prospect) => {
    setAddingToPipeline(prev => ({ ...prev, [prospect.id]: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('convert_prospect_to_deal', {
        p_prospect_id: prospect.id,
        p_user_id: session.user.id
      });
      if (error) throw error;

      showToast(`${prospect.name} added to pipeline! AI is researching sister chapels…`);
      await loadProspects(selectedRun);
      if (onAddToPipeline && data) onAddToPipeline(data);

      // Background AI enrichment — pulls in additional locations the
      // single-prospect record didn't capture, fills any blank fields.
      // Fire-and-forget so the user isn't blocked.
      if (data) {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-enrich-deal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ deal_id: data }),
        }).catch(e => console.warn('ai-enrich-deal background call failed:', e.message));
      }
    } catch (err) {
      console.error('Error adding to pipeline:', err);
      showToast(err.message || 'Failed to add to pipeline', 'error');
    } finally {
      setAddingToPipeline(prev => ({ ...prev, [prospect.id]: false }));
    }
  };

  const handleDismissProspect = async (prospect) => {
    try {
      const { error } = await supabase
        .from('discovered_prospects')
        .update({ status: 'dismissed', updated_at: new Date().toISOString() })
        .eq('id', prospect.id);
      if (error) throw error;
      showToast('Prospect dismissed');
      await loadProspects(selectedRun);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Free status transition for any prospect — used by the status menu so the
  // user can mark a prospect as Reviewing (intermediate research state),
  // un-dismiss something they changed their mind about, etc. Adding to
  // pipeline still goes through handleAddToPipeline because that creates
  // an actual deal record.
  const handleSetProspectStatus = async (prospect, nextStatus) => {
    if (nextStatus === prospect.status) return;
    try {
      const { error } = await supabase
        .from('discovered_prospects')
        .update({ status: nextStatus, updated_at: new Date().toISOString() })
        .eq('id', prospect.id);
      if (error) throw error;
      const labels = { new: 'New', reviewing: 'Reviewing', dismissed: 'Dismissed', added_to_pipeline: 'In Pipeline' };
      showToast(`Marked as ${labels[nextStatus] || nextStatus}`);
      await loadProspects(selectedRun);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Normalize a prospect's name + city for grouping near-duplicates.
  // Strips punctuation, common suffixes (funeral home, inc, llc, co, &, the),
  // so "Toland-Herzig Funeral Home" and "Toland Herzig Funeral Homes" collapse.
  const normalizeKey = (name, city) => {
    const stripWord = (s) => s
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\b(funeral|home|homes|chapel|chapels|mortuary|mortuaries|cremation|crematory|crematories|services|service|inc|llc|co|company|the|of|and)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return `${stripWord(name || '')}|${(city || '').toLowerCase().trim()}`;
  };

  // Group prospects by normalized (name, city) — any group with 2+ members
  // is a duplicate cluster. We surface a count + open a modal to resolve.
  const dedupeGroups = React.useMemo(() => {
    const map = new Map();
    for (const p of prospects) {
      if (!p.name) continue;
      const key = normalizeKey(p.name, p.city);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.values()).filter(group => group.length > 1);
  }, [prospects]);

  const duplicateCount = dedupeGroups.reduce((sum, g) => sum + g.length - 1, 0);

  // Filter prospects
  const filteredProspects = prospects.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (scoreFilter === 'high' && p.acquisition_score < 7) return false;
    if (scoreFilter === 'medium' && (p.acquisition_score < 4 || p.acquisition_score >= 7)) return false;
    if (selectedRun && p.discovery_run_id !== selectedRun) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const hay = [p.name, p.city, p.state, p.address, p.owner_name].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Delete every prospect in a list except the chosen "keep" id.
  const handleResolveDuplicates = async (keepIds) => {
    setDedupeBusy(true);
    try {
      const toDelete = [];
      for (const group of dedupeGroups) {
        const keepId = keepIds[normalizeKey(group[0].name, group[0].city)] || pickBestProspect(group).id;
        for (const p of group) if (p.id !== keepId) toDelete.push(p.id);
      }
      if (toDelete.length === 0) {
        showToast('Nothing to remove');
        setShowDedupeModal(false);
        return;
      }
      const { error } = await supabase
        .from('discovered_prospects')
        .delete()
        .in('id', toDelete);
      if (error) throw error;
      showToast(`Removed ${toDelete.length} duplicate${toDelete.length === 1 ? '' : 's'}`);
      setShowDedupeModal(false);
      await loadProspects(selectedRun);
    } catch (err) {
      console.error('Dedupe error:', err);
      showToast(err.message || 'Failed to remove duplicates', 'error');
    } finally {
      setDedupeBusy(false);
    }
  };

  // Pick the prospect to keep in a dup group — prefer in-pipeline, then highest
  // score, then most recently updated.
  const pickBestProspect = (group) => {
    const ranked = [...group].sort((a, b) => {
      const inPipelineA = a.status === 'added_to_pipeline' ? 1 : 0;
      const inPipelineB = b.status === 'added_to_pipeline' ? 1 : 0;
      if (inPipelineA !== inPipelineB) return inPipelineB - inPipelineA;
      const scoreA = a.acquisition_score || 0;
      const scoreB = b.acquisition_score || 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    });
    return ranked[0];
  };

  const getScoreColor = (score) => {
    if (score >= 7) return 'text-green-600 bg-green-100';
    if (score >= 4) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 7) return 'High Potential';
    if (score >= 4) return 'Medium';
    return 'Low';
  };

  return (
    <div className="space-y-6">
      {/* Discovery Form — the page heading ("Prospects") is rendered by App.jsx */}
      <div
        className="rounded-2xl p-5 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.06) 0%, rgba(167, 139, 250, 0.03) 100%)',
          border: '1px solid rgba(124, 58, 237, 0.2)',
        }}
      >
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">
          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
               style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)', boxShadow: '0 2px 6px rgba(124, 58, 237, 0.3)' }}>
            <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.2} />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#5B21B6' }}>
            Search Parameters
          </span>
          {/* Mode toggle: area vs. name search */}
          <div className="ml-auto inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid #DDD6FE' }}>
            {[
              { id: 'area', label: 'By area', icon: MapPin },
              { id: 'name', label: 'By name', icon: Search },
            ].map(opt => {
              const Icon = opt.icon;
              const active = searchMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSearchMode(opt.id)}
                  className="h-8 inline-flex items-center gap-1.5 px-3 text-[12px] font-semibold transition-colors"
                  style={active
                    ? { background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)', color: 'white' }
                    : { backgroundColor: 'white', color: '#7C3AED' }}
                >
                  <Icon className="w-3.5 h-3.5" /> {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {searchMode === 'area' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <ProspectField label="State">
              <select value={selectedState} onChange={e => setSelectedState(e.target.value)} className="prospect-input">
                <option value="">Select a state…</option>
                {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </ProspectField>
            <ProspectField label="Center City" optional>
              <input type="text" value={centerCity} onChange={e => setCenterCity(e.target.value)} placeholder="e.g. Pittsburgh" className="prospect-input" />
            </ProspectField>
            <ProspectField label="Search Radius">
              <select value={radiusMiles} onChange={e => setRadiusMiles(parseInt(e.target.value))} className="prospect-input">
                <option value={25}>25 miles</option>
                <option value={50}>50 miles</option>
                <option value={75}>75 miles</option>
                <option value={100}>100 miles</option>
                <option value={150}>150 miles (large region)</option>
              </select>
            </ProspectField>
            <ProspectField label="Max Results">
              <select value={maxResults} onChange={e => setMaxResults(parseInt(e.target.value))} className="prospect-input">
                <option value={10}>10 prospects</option>
                <option value={15}>15 prospects</option>
                <option value={20}>20 prospects</option>
                <option value={25}>25 prospects</option>
              </select>
            </ProspectField>
            <ProspectField label="Min Annual Calls" optional>
              <input type="number" value={minCalls} onChange={e => setMinCalls(e.target.value)} placeholder="e.g. 75" className="prospect-input" />
            </ProspectField>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ProspectField label="Funeral Home Name">
              <input
                type="text"
                value={prospectName}
                onChange={e => setProspectName(e.target.value)}
                placeholder="e.g. Toland-Herzig Funeral Home"
                className="prospect-input"
              />
            </ProspectField>
            <ProspectField label="City" optional>
              <input
                type="text"
                value={prospectCity}
                onChange={e => setProspectCity(e.target.value)}
                placeholder="e.g. Dover"
                className="prospect-input"
              />
            </ProspectField>
            <ProspectField label="State" optional>
              <select value={prospectStateInput} onChange={e => setProspectStateInput(e.target.value)} className="prospect-input">
                <option value="">Any state…</option>
                {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </ProspectField>
          </div>
        )}

        <style>{`
          .prospect-input {
            width: 100%;
            height: 2.25rem;
            border-radius: 0.5rem;
            padding: 0 0.75rem;
            border: 1px solid #E5E9F0;
            background-color: white;
            font-size: 0.875rem;
            color: #0F172A;
            outline: none;
            transition: border-color 0.12s, box-shadow 0.12s;
          }
          .prospect-input:focus {
            border-color: #7C3AED;
            box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
          }
        `}</style>

        {/* Checkboxes */}
        <div className="flex flex-wrap gap-5 mt-4">
          <label className="flex items-center gap-2 text-[13px] cursor-pointer" style={{ color: '#334155' }}>
            <input
              type="checkbox"
              checked={excludeConsolidators}
              onChange={e => setExcludeConsolidators(e.target.checked)}
              className="rounded accent-[#7C3AED]"
            />
            Exclude consolidators (SCI, Carriage, etc.)
          </label>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer" style={{ color: '#334155' }}>
            <input
              type="checkbox"
              checked={requireCrematory}
              onChange={e => setRequireCrematory(e.target.checked)}
              className="rounded accent-[#7C3AED]"
            />
            Must have crematory
          </label>
        </div>

        {/* Discovery Button */}
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <button
            onClick={handleDiscovery}
            disabled={isDiscovering || (searchMode === 'area' ? (!selectedState && !region) : !prospectName.trim())}
            className="h-10 inline-flex items-center gap-2 px-5 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
              boxShadow: '0 1px 2px rgba(124, 58, 237, 0.35), 0 2px 4px rgba(124, 58, 237, 0.18)',
            }}
          >
            {isDiscovering ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Discovering…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Start AI Discovery</>
            )}
          </button>
          {discoveryProgress && (
            <span className="text-sm animate-pulse" style={{ color: '#7C3AED' }}>{discoveryProgress}</span>
          )}
        </div>

        <p className="text-[11px] mt-3" style={{ color: '#7C3AED', opacity: 0.75 }}>
          AI web search · typically 30–90 seconds · ~$0.02–0.05 per run
        </p>

        {/* Scoring Rubric Toggle */}
        <div className="mt-4 border-t border-purple-200 pt-3">
          <button
            onClick={() => setShowRubric(v => !v)}
            className="flex items-center gap-2 text-sm text-purple-700 hover:text-purple-900 transition-colors"
          >
            <Info className="w-4 h-4" />
            {showRubric ? 'Hide' : 'View'} Scoring Rubric
            {showRubric ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>

          {showRubric && (
            <div className="mt-3 bg-white border border-purple-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-purple-100 border-b border-purple-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-purple-900">Acquisition Score Rubric</span>
                <span className="text-xs text-purple-700">Max: {MAX_SCORE}/10</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b">
                    <th className="text-left px-4 py-2 font-medium">Signal</th>
                    <th className="text-left px-4 py-2 font-medium">Points</th>
                    <th className="text-left px-4 py-2 font-medium hidden sm:table-cell">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {SCORING_RUBRIC.map((row, i) => (
                    <tr key={i} className={`border-b last:border-0 ${row.computed ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-2 text-gray-800 flex items-center gap-1.5">
                        {row.computed && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">computed</span>
                        )}
                        {row.label}
                      </td>
                      <td className="px-4 py-2 font-semibold text-purple-700">+{row.points}</td>
                      <td className="px-4 py-2 text-gray-500 hidden sm:table-cell">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
                AI-assigned signals are researched during discovery. <span className="font-medium text-blue-700">Computed</span> signals are calculated locally using your LFG Locations data and do not require re-running a search.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="bg-white border rounded-lg">
        {/* Results Header with Filters */}
        <div className="border-b px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-medium" style={{ color: displayPrimary }}>
              Discovered Prospects
            </h3>
            <span className="text-sm px-2 py-0.5 bg-gray-100 rounded-full" style={{ color: displaySecondary }}>
              {filteredProspects.length} {filteredProspects.length === 1 ? 'result' : 'results'}
            </span>
            {duplicateCount > 0 && (
              <button
                onClick={() => setShowDedupeModal(true)}
                className="text-xs px-2 py-1 rounded-md font-medium inline-flex items-center gap-1.5 transition-colors"
                style={{ backgroundColor: '#FEF3C7', color: '#A16207', border: '1px solid #FDE68A' }}
                title="Some prospects appear more than once. Review and merge."
              >
                <AlertTriangle className="w-3 h-3" />
                {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'} · Review
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94A3B8' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name, city…"
                className="text-sm border rounded pl-7 pr-7 py-1 w-56 outline-none focus:border-purple-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
                  title="Clear"
                >
                  <X className="w-3 h-3" style={{ color: '#94A3B8' }} />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="added_to_pipeline">In Pipeline</option>
              <option value="dismissed">Dismissed</option>
            </select>

            {/* Score Filter */}
            <select
              value={scoreFilter}
              onChange={e => setScoreFilter(e.target.value)}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="all">All Scores</option>
              <option value="high">High (7+)</option>
              <option value="medium">Medium (4–6)</option>
            </select>

            {/* Run Filter */}
            {pastRuns.length > 0 && (
              <select
                value={selectedRun || ''}
                onChange={e => {
                  const val = e.target.value || null;
                  setSelectedRun(val);
                  loadProspects(val);
                }}
                className="text-sm border rounded px-2 py-1"
              >
                <option value="">All Runs</option>
                {pastRuns.map(run => (
                  <option key={run.id} value={run.id}>
                    {new Date(run.created_at).toLocaleDateString()} — {run.insights?.total_found ?? '?'} found
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Prospect List */}
        <div className="divide-y">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" style={{ color: displayPrimary }} />
              <p className="text-sm text-gray-500">Loading prospects...</p>
            </div>
          ) : filteredProspects.length === 0 ? (() => {
            // If a specific past run is selected and it has 0 prospects, show
            // the run's error_message (if any) so the user knows it was a
            // truncation/parsing failure, not "no matches found". The new
            // extractor handles truncation, so re-running typically succeeds.
            const run = selectedRun ? pastRuns.find(r => r.id === selectedRun) : null;
            const runFailed = run && (run.error_message || (run.insights?.recovery_needed));
            return (
              <div className="p-8 text-center">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">
                  {runFailed ? 'This search returned no usable results' : 'No prospects found'}
                </p>
                {runFailed ? (
                  <>
                    <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                      {run.error_message || 'The original response was truncated and prospects could not be parsed.'}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      The discovery function has been improved — re-run this search above to get fresh results.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400 mt-1">
                    {prospects.length === 0
                      ? 'Run a discovery search to find acquisition targets'
                      : 'Try adjusting your filters'}
                  </p>
                )}
              </div>
            );
          })() : (
            filteredProspects.map(prospect => {
              const displayScore = getDisplayScore(prospect);
              const lfgDist = getNearestLfgDistance(prospect);
              const isNearLfg = lfgDist !== null && lfgDist < 10;

              return (
                <div key={prospect.id} className="hover:bg-gray-50 transition-colors">
                  {/* Prospect Header Row */}
                  <div
                    className="px-4 py-3 flex items-center gap-4 cursor-pointer"
                    onClick={() => setExpandedProspect(expandedProspect === prospect.id ? null : prospect.id)}
                  >
                    {/* Expand Arrow */}
                    <div className="text-gray-400">
                      {expandedProspect === prospect.id
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                      }
                    </div>

                    {/* Score Badge */}
                    <div className={`px-2 py-1 rounded-lg text-sm font-semibold min-w-[52px] text-center ${getScoreColor(displayScore)}`}>
                      {displayScore % 1 === 0 ? displayScore : displayScore.toFixed(1)}/10
                    </div>

                    {/* Name & Location */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{prospect.name}</div>
                      <div className="text-sm text-gray-500 flex items-center gap-1 flex-wrap">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span>{[prospect.city, prospect.state].filter(Boolean).join(', ')}</span>
                        {lfgDist !== null ? (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            isNearLfg
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            ~{lfgDist} mi from LFG
                          </span>
                        ) : prospectCoords[prospect.id] === undefined && prospect.city ? (
                          <span className="text-xs text-gray-400">calculating...</span>
                        ) : null}
                      </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="hidden md:flex items-center gap-4 text-sm">
                      {prospect.estimated_annual_calls && (
                        <div className="flex items-center gap-1 text-gray-600">
                          <TrendingUp className="w-4 h-4" />
                          ~{prospect.estimated_annual_calls} calls/yr
                        </div>
                      )}
                      {prospect.has_crematory && (
                        <div className="flex items-center gap-1 text-orange-600">
                          <Flame className="w-4 h-4" />
                          Crematory
                        </div>
                      )}
                      {prospect.google_rating && (
                        <div className="flex items-center gap-1 text-yellow-600">
                          <Star className="w-4 h-4 fill-current" />
                          {prospect.google_rating}
                        </div>
                      )}
                    </div>

                    {/* Status menu — clickable badge that opens a dropdown
                        for setting any of the four states. Add-to-pipeline
                        still routes through handleAddToPipeline because that
                        also creates the actual deal. */}
                    <div onClick={e => e.stopPropagation()}>
                      <ProspectStatusMenu
                        prospect={prospect}
                        addingToPipeline={!!addingToPipeline[prospect.id]}
                        onAddToPipeline={() => handleAddToPipeline(prospect)}
                        onSetStatus={(s) => handleSetProspectStatus(prospect, s)}
                      />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedProspect === prospect.id && (
                    <div className="px-4 pb-4 pl-12 space-y-4 bg-gray-50 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
                        {/* Contact Info */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-gray-700">Contact Info</h4>
                          {prospect.address && (
                            <div className="text-sm flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                              <span>
                                {prospect.address}<br />
                                {prospect.city}, {prospect.state} {prospect.zip}
                              </span>
                            </div>
                          )}
                          {lfgDist !== null && (
                            <div className={`text-sm flex items-center gap-2 ${isNearLfg ? 'text-blue-700' : 'text-gray-500'}`}>
                              <Building2 className="w-4 h-4" />
                              {isNearLfg
                                ? `~${lfgDist} mi from nearest LFG property (+0.5 pts)`
                                : `~${lfgDist} mi from nearest LFG property`}
                            </div>
                          )}
                          {prospect.phone && (
                            <div className="text-sm flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              {prospect.phone}
                            </div>
                          )}
                          {prospect.website && (
                            <div className="text-sm flex items-center gap-2">
                              <Globe className="w-4 h-4 text-gray-400" />
                              <a
                                href={prospect.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                Website <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Owner Info */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-gray-700">Owner Info</h4>
                          {prospect.owner_name ? (
                            <>
                              <div className="text-sm flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-400" />
                                {prospect.owner_name}
                              </div>
                              {prospect.owner_age_estimate && (
                                <div className="text-sm text-gray-600">
                                  Estimated age: ~{prospect.owner_age_estimate}
                                  {prospect.owner_age_estimate >= 60 && (
                                    <span className="ml-2 text-green-600 text-xs">✓ Succession opportunity</span>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-sm text-gray-400">Owner info not found</div>
                          )}
                        </div>

                        {/* Metrics */}
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-gray-700">Metrics</h4>
                          <div className="text-sm space-y-1">
                            {prospect.estimated_annual_calls && (
                              <div>Est. Calls: <strong>~{prospect.estimated_annual_calls}/year</strong></div>
                            )}
                            {prospect.google_rating && (
                              <div className="flex items-center gap-1">
                                Google: <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                <strong>{prospect.google_rating}</strong>
                                {prospect.google_review_count && (
                                  <span className="text-gray-500">({prospect.google_review_count} reviews)</span>
                                )}
                              </div>
                            )}
                            <div>
                              Crematory: <strong>
                                {prospect.has_crematory ? 'Yes' : prospect.has_crematory === false ? 'No' : 'Unknown'}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Score Breakdown */}
                      <div>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">Score Breakdown</h4>
                        <div className="flex flex-wrap gap-2">
                          {prospect.acquisition_signals && prospect.acquisition_signals.map((signal, i) => (
                            <span key={i} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                              {signal}
                            </span>
                          ))}
                          {isNearLfg && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                              Within 10 mi of LFG (+0.5)
                            </span>
                          )}
                          {(!prospect.acquisition_signals?.length && !isNearLfg) && (
                            <span className="text-xs text-gray-400">No signals recorded</span>
                          )}
                        </div>
                      </div>

                      {/* AI Notes */}
                      {prospect.notes && (
                        <div>
                          <h4 className="font-medium text-sm text-gray-700 mb-2">AI Analysis</h4>
                          <p className="text-sm text-gray-600 bg-white p-3 rounded border">{prospect.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Dedupe modal — review near-duplicate prospect clusters and pick a
          single record to keep. Defaults to "best" (in-pipeline > highest
          score > most recent). The rest are deleted. */}
      {showDedupeModal && (
        <DedupeModal
          groups={dedupeGroups}
          pickBest={pickBestProspect}
          normalizeKey={normalizeKey}
          busy={dedupeBusy}
          onClose={() => setShowDedupeModal(false)}
          onResolve={handleResolveDuplicates}
        />
      )}

      {/* Past Discovery Runs Summary */}
      {pastRuns.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-3" style={{ color: displayPrimary }}>Recent Discovery Runs</h3>
          <div className="space-y-2">
            {pastRuns.slice(0, 5).map(run => (
              <div
                key={run.id}
                className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                  selectedRun === run.id ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50'
                }`}
                onClick={() => {
                  setSelectedRun(run.id);
                  loadProspects(run.id);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    run.status === 'complete' ? 'bg-green-500' :
                    run.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
                    'bg-red-500'
                  }`} />
                  <div>
                    <div className="text-sm font-medium">
                      {run.input_params?.region || 'Unknown Region'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(run.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {run.insights?.total_found ?? 0} found · {run.insights?.elapsed_seconds?.toFixed(0) ?? '?'}s
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DedupeModal({ groups, pickBest, normalizeKey, busy, onClose, onResolve }) {
  // Map of normalized group key → prospect.id that should be kept.
  // Initialized to the "best" prospect per group (pipeline > score > recency).
  const [keepIds, setKeepIds] = useState(() => {
    const init = {};
    for (const g of groups) {
      const best = pickBest(g);
      init[normalizeKey(g[0].name, g[0].city)] = best.id;
    }
    return init;
  });

  const totalToDelete = groups.reduce((sum, g) => sum + g.length - 1, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold" style={{ color: '#0F172A' }}>Review Duplicate Prospects</h3>
            <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
              {groups.length} duplicate cluster{groups.length === 1 ? '' : 's'} · {totalToDelete} prospect{totalToDelete === 1 ? '' : 's'} will be removed.
              Pick the record to keep from each group.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" disabled={busy}>
            <X className="w-4 h-4" style={{ color: '#64748B' }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {groups.map((group, gi) => {
            const key = normalizeKey(group[0].name, group[0].city);
            const keepId = keepIds[key];
            return (
              <div key={gi} className="border rounded-lg overflow-hidden" style={{ borderColor: '#E5E9F0' }}>
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                     style={{ backgroundColor: '#FAFBFD', color: '#64748B', borderBottom: '1px solid #F0F2F6' }}>
                  {group[0].name} {group[0].city ? `· ${group[0].city}, ${group[0].state || ''}` : ''} · {group.length} copies
                </div>
                <div className="divide-y">
                  {group.map(p => {
                    const isKeep = p.id === keepId;
                    return (
                      <label
                        key={p.id}
                        className="flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors"
                        style={{ backgroundColor: isKeep ? '#ECFDF5' : 'white' }}
                      >
                        <input
                          type="radio"
                          name={`keep-${gi}`}
                          checked={isKeep}
                          onChange={() => setKeepIds(prev => ({ ...prev, [key]: p.id }))}
                          className="mt-1 accent-emerald-600"
                          disabled={busy}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium" style={{ color: '#0F172A' }}>{p.name}</span>
                            {p.status === 'added_to_pipeline' && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                    style={{ backgroundColor: '#ECFDF5', color: '#047857' }}>
                                In Pipeline
                              </span>
                            )}
                            {p.acquisition_score != null && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                                    style={{ backgroundColor: '#F1F5F9', color: '#475569' }}>
                                Score {p.acquisition_score}
                              </span>
                            )}
                            {isKeep && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                                    style={{ backgroundColor: '#10B981', color: 'white' }}>
                                <Check className="w-2.5 h-2.5" /> KEEP
                              </span>
                            )}
                          </div>
                          <div className="text-xs mt-0.5" style={{ color: '#64748B' }}>
                            {[p.address, p.city, p.state, p.zip].filter(Boolean).join(', ') || '—'}
                          </div>
                          <div className="text-[11px] mt-0.5" style={{ color: '#94A3B8' }}>
                            Added {new Date(p.created_at).toLocaleDateString()}
                            {p.owner_name && ` · Owner: ${p.owner_name}`}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 border rounded text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onResolve(keepIds)}
            disabled={busy || totalToDelete === 0}
            className="px-4 py-2 rounded text-sm font-semibold text-white transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ backgroundColor: '#DC2626' }}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            Remove {totalToDelete} duplicate{totalToDelete === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
